import { brand, shadows } from '@alison/ui';
import CheckOutlined from '@mui/icons-material/CheckOutlined';
import ExpandLessOutlined from '@mui/icons-material/ExpandLessOutlined';
import ExpandMoreOutlined from '@mui/icons-material/ExpandMoreOutlined';
import LinkOutlined from '@mui/icons-material/LinkOutlined';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import { alpha, type Theme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { Fragment, type ReactNode, useRef, useState } from 'react';
import { Pill, PRIMARY_PILL_SX } from './Pill.js';
import type { ActiveState } from './runState.js';
import { type MapRow, visibleRows } from './sessionRows.js';

const DOT_BOX = 18;
const DOT_SIZE = 8;
const SPINNER_SIZE = 14;

const FLOW_NAME_MAX_WIDTH = 160;

const CHIP_SX = { px: 2, py: 1, borderRadius: 0.5, flexShrink: 0 };

const LABEL_SX = { fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' };

const HEADING_SX = { fontSize: 10, fontWeight: 600, color: 'text.muted', whiteSpace: 'nowrap' };

const POPOVER_WIDTH = 300;

const STATUS_LABEL: Record<DotStatus, string> = {
  done: 'done',
  chained: 'done',
  current: 'current',
  future: 'up next',
  running: 'running',
  attention: 'needs attention',
};

const DOT_SX: Record<DotStatus, (theme: Theme) => object> = {
  done: (theme) => ({ bgcolor: theme.palette.success.dark }),
  chained: (theme) => ({ bgcolor: theme.palette.success.dark, opacity: 0.45 }),
  current: (theme) => ({
    bgcolor: theme.palette.primary.main,
    boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`,
  }),
  future: (theme) => ({ border: `1.5px solid ${theme.palette.border.strong}` }),
  running: () => ({}),
  attention: (theme) => ({
    bgcolor: theme.palette.warning.main,
    boxShadow: `0 0 0 3px ${alpha(theme.palette.warning.main, 0.5)}`,
  }),
};

const HOVER_BG: Record<DotStatus, (theme: Theme) => string> = {
  done: (theme) => alpha(theme.palette.success.dark, 0.1),
  chained: (theme) => alpha(theme.palette.success.dark, 0.1),
  current: (theme) => theme.palette.background.platform,
  future: (theme) => alpha(theme.palette.primary.main, 0.1),
  running: (theme) => theme.palette.background.platform,
  attention: (theme) => theme.palette.background.platform,
};

type DotStatus = 'done' | 'chained' | 'current' | 'future' | 'running' | 'attention';

export interface StepperFlow {
  name: string;
  steps: MapRow[];
}

interface DotProps {
  status: DotStatus;
  title: string;
  index: number;
}

interface PopoverRowProps {
  step: MapRow;
  onEdit: (() => void) | null;
}

interface FlowGroupProps {
  heading: string;
  chained: boolean;
  steps: MapRow[];
  editFor: (step: MapRow) => (() => void) | null;
}

interface WorkflowStepperProps {
  flows: StepperFlow[];
  activeState?: ActiveState;
  onEdit?: (flowIndex: number) => void;
}

export function stepperFlows(rows: MapRow[], starts: number[], names: string[]): StepperFlow[] {
  return names.map((name, index) => ({
    name,
    steps: visibleRows(rows.slice(starts[index] ?? rows.length, starts[index + 1] ?? rows.length)),
  }));
}

// Numbers across the whole chain, like the step card and the session map; `before` is the steps of earlier flows.
export function stepLabel(steps: MapRow[], before = 0): string {
  const activeIndex = Math.max(
    steps.findIndex((step) => step.status === 'active'),
    0,
  );
  return `Step ${before + activeIndex + 1} of ${before + steps.length} · ${steps[activeIndex]?.title ?? ''}`;
}

export function dotStatus(
  step: MapRow,
  inCurrentFlow: boolean,
  activeState: ActiveState,
): DotStatus {
  if (!inCurrentFlow) return 'chained';
  if (step.status === 'done') return 'done';
  if (step.status === 'pending') return 'future';
  return activeState ?? 'current';
}

function Dot({ status, title, index }: DotProps): ReactNode {
  return (
    <Tooltip title={title}>
      <Box
        role="img"
        tabIndex={0}
        aria-label={`Step ${index}, ${title}, ${STATUS_LABEL[status]}`}
        data-status={status}
        sx={(theme) => ({
          width: DOT_BOX,
          height: DOT_BOX,
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          '&:hover': { bgcolor: HOVER_BG[status](theme) },
        })}
      >
        {status === 'running' ? (
          <CircularProgress size={SPINNER_SIZE} />
        ) : (
          <Box
            sx={(theme) => ({
              width: DOT_SIZE,
              height: DOT_SIZE,
              boxSizing: 'border-box',
              borderRadius: '50%',
              ...DOT_SX[status](theme),
            })}
          />
        )}
      </Box>
    </Tooltip>
  );
}

function PopoverRow({ step, onEdit }: PopoverRowProps): ReactNode {
  return (
    <Stack
      direction="row"
      sx={{
        gap: 2,
        px: 2,
        py: 1.5,
        alignItems: 'center',
        borderRadius: 1.5,
        '&:hover': { bgcolor: 'background.platform' },
      }}
    >
      <CheckOutlined sx={{ fontSize: 16, color: 'success.dark' }} />
      <Stack sx={{ gap: '1px', minWidth: 0, flexGrow: 1 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 600 }} noWrap>
          {step.title}
        </Typography>
        {step.line === null ? null : (
          <Typography sx={{ fontSize: 10, color: 'text.muted' }} noWrap>
            {step.line}
          </Typography>
        )}
      </Stack>
      {onEdit === null ? null : (
        <Link
          component="button"
          type="button"
          underline="none"
          onClick={onEdit}
          aria-label={`Edit ${step.title}`}
          sx={{ fontSize: 10, fontWeight: 600, flexShrink: 0 }}
        >
          Edit
        </Link>
      )}
    </Stack>
  );
}

function FlowGroup({ heading, chained, steps, editFor }: FlowGroupProps): ReactNode {
  return (
    <>
      <Stack direction="row" sx={{ gap: 1.5, px: 2, pt: 1.5, pb: 0.5, alignItems: 'center' }}>
        {chained ? (
          <LinkOutlined sx={{ fontSize: 16, color: brand.violet, transform: 'rotate(-45deg)' }} />
        ) : null}
        <Typography sx={HEADING_SX} noWrap>
          {heading}
        </Typography>
      </Stack>
      {steps.map((step) => (
        <PopoverRow key={step.key} step={step} onEdit={editFor(step)} />
      ))}
    </>
  );
}

export function WorkflowStepper({
  flows,
  activeState = null,
  onEdit,
}: WorkflowStepperProps): ReactNode {
  const [open, setOpen] = useState(false);
  const chevron = useRef<HTMLButtonElement>(null);
  const current = flows[flows.length - 1];
  if (current === undefined || current.steps.length === 0) return null;
  const chained = flows.slice(0, -1);
  const starts = flows.map((_, index) =>
    flows.slice(0, index).reduce((count, flow) => count + flow.steps.length, 0),
  );
  const doneInCurrent = current.steps.filter((step) => step.status === 'done');
  const hasDone = chained.length > 0 || doneInCurrent.length > 0;
  const editFor =
    (flowIndex: number) =>
    (step: MapRow): (() => void) | null => {
      if (onEdit === undefined || (flowIndex === chained.length && !step.editable)) return null;
      return () => {
        setOpen(false);
        onEdit(flowIndex);
      };
    };

  return (
    <Stack
      direction="row"
      data-testid="workflow-stepper"
      sx={{ flexWrap: 'wrap', columnGap: 2, rowGap: 1, py: 1, alignItems: 'center', minWidth: 0 }}
    >
      {chained.map((flow) => (
        <Stack
          direction="row"
          key={flow.steps[0]?.key ?? flow.name}
          sx={{ gap: 2, alignItems: 'center', flexShrink: 0 }}
        >
          <ButtonBase
            onClick={() => setOpen(true)}
            aria-label={`${flow.name}, show completed steps`}
            sx={[
              CHIP_SX,
              { gap: 1, border: '1px solid', borderColor: 'divider' },
              { '&:hover': { bgcolor: 'background.platform' } },
              open ? { bgcolor: 'primary.bg', borderColor: 'primary.main' } : {},
            ]}
          >
            <CheckOutlined sx={{ fontSize: 16, color: 'success.dark' }} />
            <Typography
              sx={[LABEL_SX, { maxWidth: FLOW_NAME_MAX_WIDTH, color: 'text.muted' }]}
              noWrap
            >
              {flow.name}
            </Typography>
            <Typography sx={[LABEL_SX, { flexShrink: 0, fontWeight: 400, color: 'text.muted' }]}>
              · {flow.steps.length} {flow.steps.length === 1 ? 'step' : 'steps'}
            </Typography>
          </ButtonBase>
          <LinkOutlined sx={{ fontSize: 16, color: brand.violet, transform: 'rotate(-45deg)' }} />
        </Stack>
      ))}

      <Pill
        sx={[
          PRIMARY_PILL_SX,
          CHIP_SX,
          LABEL_SX,
          { maxWidth: FLOW_NAME_MAX_WIDTH, overflow: 'hidden', textOverflow: 'ellipsis' },
        ]}
      >
        {current.name}
      </Pill>
      <Typography
        sx={{ flexGrow: 1, maxWidth: '100%', fontWeight: 600, color: 'background.icon' }}
        noWrap
      >
        {stepLabel(current.steps, starts.at(-1))}
      </Typography>

      <Stack direction="row" sx={{ ml: 'auto', alignItems: 'center', flexShrink: 0 }}>
        {flows.map((flow, index) => (
          <Fragment key={flow.steps[0]?.key ?? flow.name}>
            {index === 0 ? null : <Box sx={{ width: '1px', height: 10, bgcolor: 'divider' }} />}
            {flow.steps.map((step, stepIndex) => (
              <Dot
                key={step.key}
                index={(starts[index] ?? 0) + stepIndex + 1}
                title={step.title}
                status={dotStatus(step, flow === current, activeState)}
              />
            ))}
          </Fragment>
        ))}
        {hasDone ? (
          <IconButton
            size="small"
            aria-label="Completed steps"
            aria-expanded={open}
            ref={chevron}
            onClick={() => setOpen(true)}
            sx={[
              { ml: 2, width: 22, height: 22, borderRadius: 1, color: 'text.muted' },
              open ? { bgcolor: 'primary.bg', color: 'primary.main' } : {},
            ]}
          >
            {open ? (
              <ExpandLessOutlined sx={{ fontSize: 16 }} />
            ) : (
              <ExpandMoreOutlined sx={{ fontSize: 16 }} />
            )}
          </IconButton>
        ) : null}
      </Stack>

      <Popover
        open={open}
        anchorEl={chevron.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: (theme) => ({
              mt: 1,
              p: 1.5,
              width: POPOVER_WIDTH,
              borderRadius: `${theme.radius.xl}px`,
              boxShadow: shadows.dropdown,
            }),
          },
        }}
      >
        {doneInCurrent.length === 0 ? null : (
          <FlowGroup
            heading={`THIS WORKFLOW · ${current.name.toUpperCase()}`}
            chained={false}
            steps={doneInCurrent}
            editFor={editFor(chained.length)}
          />
        )}
        {chained
          .map((flow, flowIndex) => ({ flow, flowIndex }))
          .reverse()
          .map(({ flow, flowIndex }, index) => (
            <Fragment key={flow.steps[0]?.key ?? flow.name}>
              {index === 0 && doneInCurrent.length === 0 ? null : <Divider sx={{ my: 0.5 }} />}
              <FlowGroup
                heading={`CAME FROM · ${flow.name.toUpperCase()}`}
                chained
                steps={flow.steps}
                editFor={editFor(flowIndex)}
              />
            </Fragment>
          ))}
      </Popover>
    </Stack>
  );
}
